import React, { useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import LazyImage from './components/LazyImage';
import './App.css';

// Import images from public folder
const logo = process.env.PUBLIC_URL + '/carelook-logo.jpg';
const phone1 = process.env.PUBLIC_URL + '/1.webp';
const phone2 = process.env.PUBLIC_URL + '/2.webp';
const oneImage = process.env.PUBLIC_URL + '/ONE.webp';
const playStoreIcon = process.env.PUBLIC_URL + '/google-play-store.webp';
const appStoreIcon = process.env.PUBLIC_URL + '/Appstore.png';
const homeServiceImage = process.env.PUBLIC_URL + '/carelook-at-home.webp';
const injectionIcon = process.env.PUBLIC_URL + '/Injection.png';
const brushIcon = process.env.PUBLIC_URL + '/brush-02.png';

// Modal Component
function Modal({ isOpen, onClose, title, children }: { 
  isOpen: boolean; 
  onClose: () => void; 
  title: string; 
  children: React.ReactNode 
}) {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose}>×</button>
        <h2>{title}</h2>
        <div className="modal-body">{children}</div>
      </motion.div>
    </motion.div>
  );
}

function App() {
  const { scrollY } = useScroll();
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);

  const heroY = useTransform(scrollY, [0, 500], [0, -250]);
  const phoneY = useTransform(scrollY, [0, 1000], [0, -300]);

  return (
    <div className="app">
      {/* Header */}
      <motion.header 
        className="header"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="header-content">
          <div className="logo-container">
            <img src={logo} alt="CareLook Logo" className="logo" />
          </div>
          <motion.div 
            className="download-text"
            animate={{ 
              opacity: [0.8, 1, 0.8]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            DOWNLOAD CARELOOK APP
          </motion.div>
        </div>
      </motion.header>

      {/* Hero Section with Banner */}
      <motion.section 
        className="hero"
        style={{ y: heroY }}
      >
        <div className="hero-content">
          <motion.div
            className="banner-container"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <LazyImage src={oneImage} alt="CareLook Banner" className="banner-image" placeholder="Loading banner..." loading="eager" />
          </motion.div>
          
          <div className="hero-text">
            <motion.h1
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="hero-title"
            >
              CARELOOK
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.7 }}
              className="hero-subtitle"
            >
              Your Perfect Beauty & Wellness Partner
            </motion.p>
          </div>
        </div>
      </motion.section>

      {/* App Features Section */}
      <motion.section 
        className="features"
        style={{ y: phoneY }}
      >
        <div className="features-content">
          <motion.div
            className="phone-showcase"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="phone-container">
              <LazyImage src={phone1} alt="Customer Side" className="phone-image left" placeholder="Loading..." />
              <motion.div 
                className="arrow-right"
                animate={{ x: [0, 20, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                →
              </motion.div>
              <LazyImage src={phone2} alt="Vendor Side" className="phone-image right" placeholder="Loading..." />
            </div>
            
            <div className="phone-labels">
              <div className="label-section">
                <span className="label-title">For Users</span>
                <p className="label-description">
                  Simply log in with your mobile number and OTP, choose the service you need, and book your appointment in seconds. That's it — enjoy professional beauty, grooming, or wellness services delivered right to your home.
                </p>
              </div>
              <div className="label-section">
                <span className="label-title">For Vendors</span>
                <p className="label-description">
                  Choose your business type and register as a service provider. Once registered, you can select from the already available services and complete a simple verification process. After your profile is approved by our team, you're ready to start serving customers. You'll have full control to manage your services, update your gallery, showcase transformations, handle payments, and much more — all in one place.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Service Highlights */}
          <motion.div
            className="service-highlights"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <div className="service-item">
              <LazyImage src={homeServiceImage} alt="Home Service" className="service-image" placeholder="Loading..." />
              <h3>Professional At-Home Services</h3>
              <p>Book beauty and wellness services at your convenience with verified professionals</p>
            </div>
            
            <motion.div 
              className="connecting-arrow"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              ↓
            </motion.div>
            
            <div className="service-item">
              <h3>Seamless Experience</h3>
              <p>Connect with verified professionals instantly for all your beauty and wellness needs</p>
            </div>
          </motion.div>

          {/* Additional Services */}
          <motion.div
            className="additional-services"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <div className="service-row">
              <div className="mini-service">
                <LazyImage src={brushIcon} alt="Makeup Services" className="mini-service-icon" placeholder="" />
                <h4>Beauty & Makeup</h4>
                <p>Professional makeup artists bring their expertise and high-quality brushes directly to you. From everyday looks to special occasion glam, experience flawless makeup application in the comfort of your home.</p>
              </div>
              <div className="mini-service">
                <LazyImage src={injectionIcon} alt="Health Services" className="mini-service-icon" placeholder="" />
                <h4>Health & Diagnostics</h4>
                <p>Convenient at-home health services including blood sample collection, diagnostic tests, and wellness checkups. Professional healthcare providers ensure safe and hygienic procedures at your doorstep.</p>
              </div>
            </div>
          </motion.div>

        </div>
      </motion.section>

      {/* Download Section */}
      <motion.section
        className="download-section"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <h2>Get the App Now</h2>
        <div className="download-buttons">
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <LazyImage 
              src={playStoreIcon} 
              alt="Get it on Google Play" 
              className="download-btn"
              placeholder=""
            />
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <LazyImage 
              src={appStoreIcon} 
              alt="Download on App Store" 
              className="download-btn"
              placeholder=""
            />
          </motion.div>
        </div>
      </motion.section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-links">
            <button onClick={() => setIsTermsOpen(true)} className="footer-link">
              Terms & Conditions
            </button>
            <span className="separator">|</span>
            <button onClick={() => setIsPrivacyOpen(true)} className="footer-link">
              Privacy Policy
            </button>
            <span className="separator">|</span>
            <button onClick={() => setIsPaymentOpen(true)} className="footer-link">
              Payment Policies
            </button>
          </div>
          <div className="footer-owner">
            <p>Owner: Tushar Gautam</p>
            <p>&copy; 2024 CareLook. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <Modal 
        isOpen={isTermsOpen} 
        onClose={() => setIsTermsOpen(false)} 
        title="Terms & Conditions"
      >
        <div className="legal-content">
          <h3>1. Acceptance of Terms</h3>
          <p>By using CareLook app, you agree to these terms and conditions.</p>
          
          <h3>2. Service Description</h3>
          <p>CareLook connects customers with beauty and wellness service providers for at-home services.</p>
          
          <h3>3. User Responsibilities</h3>
          <p>Users must provide accurate information and maintain account security.</p>
          
          <h3>4. Service Provider Terms</h3>
          <p>Service providers must be licensed and follow safety protocols.</p>
          
          <h3>5. Booking and Cancellation</h3>
          <p>Bookings are subject to availability. Cancellation policies apply.</p>
          
          <h3>6. Limitation of Liability</h3>
          <p>CareLook serves as a platform and is not directly responsible for service quality.</p>
        </div>
      </Modal>

      <Modal 
        isOpen={isPrivacyOpen} 
        onClose={() => setIsPrivacyOpen(false)} 
        title="Privacy Policy"
      >
        <div className="legal-content">
          <h3>1. Information We Collect</h3>
          <p>We collect personal information necessary to provide our services including name, contact details, and location.</p>
          
          <h3>2. How We Use Your Information</h3>
          <p>Your information is used to connect you with service providers and improve our platform.</p>
          
          <h3>3. Data Security</h3>
          <p>We implement industry-standard security measures to protect your personal data.</p>
          
          <h3>4. Information Sharing</h3>
          <p>We only share information necessary to facilitate bookings with service providers.</p>
          
          <h3>5. Your Rights</h3>
          <p>You have the right to access, update, or delete your personal information.</p>
          
          <h3>6. Contact Us</h3>
          <p>For privacy concerns, contact us through the app or our support channels.</p>
        </div>
      </Modal>

      <Modal 
        isOpen={isPaymentOpen} 
        onClose={() => setIsPaymentOpen(false)} 
        title="Payment Policies"
      >
        <div className="legal-content">
          <h3>1. Payment Methods</h3>
          <p>We accept various payment methods including credit cards, debit cards, and digital wallets.</p>
          
          <h3>2. Service Fees</h3>
          <p>All service fees are clearly displayed before booking confirmation.</p>
          
          <h3>3. Cancellation & Refunds</h3>
          <p>Refunds are processed according to our cancellation policy terms.</p>
          
          <h3>4. Platform Commission</h3>
          <p>CareLook charges a platform fee from service providers for facilitating bookings.</p>
          
          <h3>5. Dispute Resolution</h3>
          <p>Payment disputes are handled through our customer support system.</p>
          
          <h3>6. Security</h3>
          <p>All payment transactions are secured with industry-standard encryption.</p>
        </div>
      </Modal>
    </div>
  );
}

export default App;
